import { activeStatuses, ec, ecLean, HandBack, type JanusCtx, type Row } from '../ability/context.js';
import { runPaoeEntryOnce, type PaoeEntryRecords } from '../ability/paoe-entry-once.js';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers.js';
import { statusScalar } from '../actions/activity.js';
import type { AttackFrameWork } from '../actions/weapon-attack-reactions.js';
import { statusItemId, STATUS_IDS } from '../statuses/expiry.js';
import type { PlainWalkResult } from './plain-walk.js';
import { posCoord } from '../../shared/array-types.js';
import { consentPending } from '../ability/trigger-consent.js';

type Entry = NonNullable<PlainWalkResult['zone_entries']>[number];
const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** Zone contacts and their exit sweep share the swing's mutable frame and native transaction.
 * The next foot is never entered when the opportunity hit stops the mover. */
export function opportunityWalkZoneWork(actorId: string, walk: PlainWalkResult, halted: PlainWalkResult, strikeFoot: number,
  records: PaoeEntryRecords | undefined, standsInZone: (id: string, zoneId: string) => boolean | null,
  prefixAt: (step: number) => PlainWalkResult = () => halted): AttackFrameWork & { stoppedWalk(): PlainWalkResult | null } {
  let stop: PlainWalkResult | null = null;
  const terminal = (ctx: JanusCtx): boolean => {
    const mover = ctx.members.get(upper(actorId));
    return !!mover && (Number(ecLean(mover, 'encounter_creature_dead') ?? 0) === 1
      || Number(ecLean(mover, 'encounter_creature_deleted') ?? 0) === 1
      || activeStatuses(ctx, mover).some(status => statusItemId(status) === '8AE129AF-52D8-CCE8-706A-85767DB52740'));
  };
  const stopAt = (ctx: JanusCtx, prefix: PlainWalkResult): void => {
    const mover = ctx.members.get(upper(actorId));
    if (!mover) throw new HandBack('reaction-zone-entry:member-not-held');
    const x = Number(posCoord(mover, 'x_pos') ?? 0), y = Number(posCoord(mover, 'y_pos') ?? 0), z = Number(posCoord(mover, 'z_pos') ?? 0);
    stop = { ...prefix, destination: { x, y, z }, updates: { ...prefix.updates, x_pos: x, y_pos: y, z_pos: z },
      route: [...prefix.route.slice(0, -1), [x, y, z]] };
  };
  const run = (ctx: JanusCtx, before: boolean): void => {
    const contacts = [{ actorId, entries: walk.zone_entries ?? [] }, ...(walk.follower_zone_entries ?? [])];
    for (const { contact, entry } of contacts.flatMap(contact => contact.entries.map(entry => ({ contact, entry })))
      .sort((a, b) => a.entry.step - b.entry.step)) {
      if (stop) return;
      if ((entry.step <= strikeFoot) !== before) continue;
      if (!records) throw new HandBack('reaction-zone-entry:records-not-held');
      const member = ctx.members.get(upper(contact.actorId));
      if (!member) throw new HandBack('reaction-zone-entry:member-not-held');
      const position = member.position;
      const xyz = Object.fromEntries((['x_pos', 'y_pos', 'z_pos'] as const).map(key => [key, Number(posCoord(member, key) ?? 0)]));
      const connection = ctx.statusConnectedId;
      try {
        member.position = { x_pos: entry.point[0], y_pos: entry.point[1], z_pos: entry.elevation ?? walk.origin.z };
        Object.assign(ec(member), member.position);
        ctx.priorDispatchStatuses = new Set(ctx.statusesAdded);
        runPaoeEntryOnce(ctx, { abilityId: entry.originId, casterId: entry.casterId, enteringId: contact.actorId,
          statusConnectedId: entry.connectedId, forcedExtreme: entry.forcedExtreme }, records);
        if (consentPending(ctx.read)) throw new HandBack('outcome:trigger-consent');
        if (terminal(ctx)) stopAt(ctx, prefixAt(entry.step));
      } finally {
        // A terminal mover keeps the landing already used by death/Dying and its corpse.
        if (!stop || upper(contact.actorId) !== upper(actorId)) {
          member.position = position;
          Object.assign(ec(member), xyz);
        }
        ctx.statusConnectedId = connection;
      }
    }
  };
  return {
    stoppedWalk: () => stop,
    beforeSwing(ctx) {
      stop = null;
      run(ctx, true);
      ctx.priorDispatchStatuses = new Set(ctx.statusesAdded);
      return stop === null;
    },
    afterSwing(ctx, swingTerminal) {
      if (!stop && (swingTerminal || terminal(ctx))) stopAt(ctx, halted);
      if (!stop) run(ctx, false);
      const stopped = stop ?? walk;
      const strip = (targetId: string, status: Row): void => {
        const member = ctx.members.get(upper(targetId));
        if (member) removeStoredStatus(ctx, member, status, 'single', { skipConnectedPeerCull: true });
      };
      // Read the current frame: death may already have removed a stored row.
      for (const exit of stopped.zone_exits ?? []) {
        const member = ctx.members.get(upper(exit.targetId));
        const status = member && activeStatuses(ctx, member).find(row => upper(statusScalar(row, 'status_id')) === upper(exit.statusId));
        if (status) strip(exit.targetId, status);
      }
      const contacts: Array<{ actorId: string; entries: Entry[] }> = [
        { actorId, entries: stopped.zone_entries ?? [] }, ...(stopped.follower_zone_entries ?? [])];
      for (const contact of contacts) for (const entry of contact.entries) {
        for (const id of new Set([upper(contact.actorId), upper(entry.casterId)])) {
          const outside = id === upper(contact.actorId) ? entry.exitsAfter : standsInZone(id, entry.zoneStatusId) === false;
          const member = ctx.members.get(id);
          if (!outside || !member) continue;
          for (const status of [...activeStatuses(ctx, member)]) {
            if (upper(statusScalar(status, 'status_connected_id')) === upper(entry.connectedId)
              && statusItemId(status) !== STATUS_IDS.concentrating && !Number(statusScalar(status, 'status_aoe_source') ?? 0)) strip(id, status);
          }
        }
      }
    },
  };
}
